// src/components/MobileCashflowTable.jsx
import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { orderCashflowFields, isCouponField, formatCouponPercent } from '../utils/cashflowDisplay';

const MobileCashflowTable = ({ data }) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No cashflow data available
        </Typography>
      </Box>
    );
  }

  // Get column names from the first row, ordered to match the desktop grid
  // (monthly rows lead with DATE instead of YEAR) and gated the same way
  // (an all-null/all-zero WRITEDOWN/INTEREST_SHORTFALL/PREPAY_PENALTY is
  // dropped rather than shown as a column of nothing).
  const columns = orderCashflowFields(data);

  // Format cell value
  const formatValue = (value, columnName) => {
    if (value === null || value === undefined || value === '') return '-';

    // COUPON is the effective coupon rate in percent, not a dollar amount.
    if (isCouponField(columnName)) return formatCouponPercent(value);

    // Handle date columns
    const isDateColumn = columnName === 'ASOFDT' || columnName.toLowerCase().includes('date');
    if (isDateColumn) {
      const str = String(value);
      // Handle YYYYMMDD format
      if (str.length === 8 && !isNaN(str)) {
        const year = str.slice(0, 4);
        const month = str.slice(4, 6);
        const day = str.slice(6, 8);
        return `${month}/${day}/${year}`;
      }
      // Handle YYYYMM format
      if (str.length === 6 && !isNaN(str)) {
        const year = str.slice(0, 4);
        const month = str.slice(4, 6);
        return `${month}/01/${year}`;
      }
      return value;
    }

    // Format numbers
    if (typeof value === 'number') {
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    return String(value);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: '60vh',
        overflowX: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column}
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f8fafc',
                  color: theme.palette.mode === 'dark' ? 'white' : '#1e293b',
                  fontWeight: 600,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  py: 2,
                  px: 2,
                  borderBottom: `2px solid ${theme.palette.divider}`
                }}
              >
                {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              sx={{
                '&:nth-of-type(odd)': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.5)
                    : alpha(theme.palette.primary.main, 0.02)
                },
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    fontSize: '14px',
                    py: 2,
                    px: 2,
                    whiteSpace: 'nowrap',
                    color: theme.palette.text.primary
                  }}
                >
                  {formatValue(row[column], column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MobileCashflowTable;

